import { Observable } from 'rxjs/internal/Observable';

import { API_PATH } from '@app/app.constants';
import { StudentPrivilegeSetting } from '@df/interfaces';
import { ResponseCharacters, ResponseMain } from '@app/_interfaces/responses';
import { Subject } from 'rxjs';
import { interval } from 'rxjs/internal/observable/interval';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { APIService } from '@app/_services/api.service';

export class PrivilegeService {
    public privilegeSettingSelected = new Subject<StudentPrivilegeSetting>();

    constructor(private api: APIService) {}

    public getLessonPrivilegeSettings(lessonID: number): Observable<any> {
        return this.api.get(`privileges/settings/lesson/${lessonID}/`);
    }

    public getStudentsPrivilegeSettingsForLesson(lesson_id: number): Observable<any> {
        return this.api.get(`privileges/settings-student/?settings__lesson=${lesson_id}`);
    }

    public deleteStudentPrivilegeSetting(setting_id: number): Observable<any> {
        return this.api.delete(`privileges/settings-student/${setting_id}/`);
    }

    public createStudentPrivilegeSetting(student_id: number, setting_id: number): Observable<any> {
        const data = { student: student_id, settings: setting_id };
        return this.api.post(`privileges/settings-student/`, data);
    }

    public getStudentPrivilegeSettingsForLesson(student_id: number, lesson_id: number): Observable<any> {
        const url = `privileges/settings-student/?student=${student_id}&settings__lesson=${lesson_id}`;
        return this.api.get(url);
    }

    public getStudentLessonPrivilegeSettings(studentID: number, lessonID: number): Observable<any> {
        return this.api.get(`privileges/settings-student/${studentID}/${lessonID}/`);
    }

    public getSchools(): Observable<any> {
        return this.api.get(`main/boxes_schools/`);
    }

    public getEpochs(epoch_id: number = null): Observable<any> {
        const url: string = epoch_id ? `${API_PATH.privileges.epoch}${epoch_id}/` : API_PATH.privileges.epoch;
        return this.api.get(url);
    }

    public getCharacter(character_id: number): Observable<any> {
        return this.api.get(`privileges/character/${character_id}/`);
    }

    public getCharacters(): Observable<any> {
        return this.api.get(`privileges/character/`);
    }

    getEpochCharacters(epochId: number): Observable<ResponseCharacters> {
        const url = `${API_PATH.privileges.epoch_characters}/${epochId}`;
        return this.api.get(url);
    }

    public deletePrivilegeSettingsForLesson(lesson_id: number): Observable<any> {
        return this.api.delete(`privileges/settings/delete/${lesson_id}/`);
    }

    public createPrivilegeSettings(data): Observable<any> {
        return this.api.post(`privileges/settings/`, data);
    }

    public getCharactersForEpoch(epoch_id: number): Observable<any> {
        return this.api.get(`privileges/character/?epoch=${epoch_id}`);
    }

    public getPupilPrivilege(pupil_id: number, lesson_id: number): Observable<any> {
        return this.api.get(`privileges/pupil/${pupil_id}/${lesson_id}/`);
    }

    public getPrivileges(): Observable<any> {
        return this.api.get(`privileges/privilege/`);
    }

    public getPrivilege(privilege_id): Observable<any> {
        return this.api.get(`privileges/privilege/${privilege_id}/`);
    }

    public getBoxPrivileges(box_id): Observable<any> {
        return this.api.get(`privileges/box/${box_id}/`);
    }

    public pollingGetStudentPrivilege(student_id: number, lesson_id: number, intTime: number): Observable<any> {
        return interval(intTime).pipe(
            switchMap(() =>
                this.api.get(`privileges/settings-student/?student=${student_id}&settings__lesson=${lesson_id}`)
            )
        );
    }

    public getKairosBonus(boxID: number): Observable<ResponseMain> {
        return this.api.get(`privileges/kairos-bonus/${boxID}/`);
    }

    public getKairosBonusSettings(boxID: number): Observable<ResponseMain> {
        return this.api.get(`privileges/kairos-bonus/settings/${boxID}/`);
    }

    public checkForKairosBonus(studentID: number, cardID: number): Observable<boolean> {
        return this.api.get(`privileges/kairos-bonus/check/${studentID}/${cardID}/`);
    }
}
